import { useCallback, useState } from 'react';
import { Pencil, Trash2, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { useTranslation } from '@/hooks/useTranslation';
import { useApiCall } from '@/hooks/useApiCall';
import type { ToolAliasApiClient } from '@/features/traceability/api/ToolAliasApiClient';
import type { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';
import type { AdminApiClient } from '@/features/admin/api/AdminApiClient';
import type { ToolAlias } from '@/features/traceability/api/ToolAliasApiClient.types';
import type {
  ToolAliasesResponse,
  ToolAliasSettingsResponse,
} from '@/features/traceability/api/ToolAliasApiClient.types';
import type { AllToolsResponse } from '@/features/traceability/api/TraceabilityResponseType';
import type { ListCompaniesResponse } from '@/features/admin/api/AdminApiClient';

interface Props {
  client: ToolAliasApiClient;
  traceabilityClient: TraceabilityApiClient;
  adminClient?: AdminApiClient;
  /** Locked company name for admins; undefined for super-admins (they pick via dropdown) */
  companyName?: string;
  isSuperAdmin: boolean;
  onAliasChange: () => void;
}

type FormMode = 'idle' | 'add' | 'edit';

export function ReferenceAliasTab({
  client,
  traceabilityClient,
  adminClient,
  companyName,
  isSuperAdmin,
  onAliasChange,
}: Props) {
  const { t } = useTranslation();

  // Super-admin company selection state
  const [selectedCompany, setSelectedCompany] = useState('');
  const effectiveCompany = isSuperAdmin ? selectedCompany : (companyName ?? '');

  // Companies list (super-admin only)
  const companiesCall = useApiCall<ListCompaniesResponse>({
    apiFn: useCallback(
      (signal) =>
        adminClient
          ? adminClient.listCompanies({ signal })
          : Promise.reject(new Error('no client')),
      [adminClient]
    ),
    immediate: isSuperAdmin && !!adminClient,
  });
  const companies = companiesCall.data?.companies ?? [];

  // Aliases — re-fetch when effectiveCompany changes; skip when empty
  const aliasesCall = useApiCall<ToolAliasesResponse>({
    apiFn: useCallback(
      (signal) => client.aliasReadAll(effectiveCompany || undefined, { signal }),
      [client, effectiveCompany]
    ),
    deps: [effectiveCompany],
    immediate: !!effectiveCompany,
  });

  // Settings — same pattern
  const settingsCall = useApiCall<ToolAliasSettingsResponse>({
    apiFn: useCallback(
      (signal) => client.aliasSettingsRead(effectiveCompany || undefined, { signal }),
      [client, effectiveCompany]
    ),
    deps: [effectiveCompany],
    immediate: !!effectiveCompany,
  });

  // Reference dropdown — admins use JWT-scoped fetch; super-admins pass effectiveCompany
  const toolsCall = useApiCall<AllToolsResponse>({
    apiFn: useCallback(
      (signal) => traceabilityClient.toolReadAll(effectiveCompany || undefined, { signal }),
      [traceabilityClient, effectiveCompany]
    ),
    deps: isSuperAdmin ? [effectiveCompany] : [],
    immediate: isSuperAdmin ? !!effectiveCompany : true,
  });
  const allTools = toolsCall.data?.tools ?? [];
  const distinctReferences = [
    ...new Set(allTools.flatMap((t) => (t.reference ? [t.reference] : []))),
  ].sort();

  // Display mode
  const [pendingMode, setPendingMode] = useState<string | null>(null);
  const currentMode = pendingMode ?? settingsCall.data?.displayMode ?? 'reference';
  const [savingMode, setSavingMode] = useState(false);
  const [saveModeError, setSaveModeError] = useState('');

  // Form state
  const [formMode, setFormMode] = useState<FormMode>('idle');
  const [formReference, setFormReference] = useState('');
  const [formAlias, setFormAlias] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Delete state
  const [deleteError, setDeleteError] = useState('');
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const aliases = aliasesCall.data?.toolAliases ?? [];

  const handleSaveMode = async () => {
    if (!effectiveCompany) return;
    setSavingMode(true);
    try {
      const result = await client.aliasSettingsWrite(currentMode, effectiveCompany || undefined);
      if (!result.ok) {
        setSaveModeError(t('referenceAliases.errors.default'));
        return;
      }
      setSaveModeError('');
      onAliasChange();
      settingsCall.retry();
    } finally {
      setSavingMode(false);
    }
  };

  const openAdd = () => {
    setFormMode('add');
    setFormReference('');
    setFormAlias('');
    setFormError('');
  };
  const openEdit = (alias: ToolAlias) => {
    setFormMode('edit');
    setFormReference(alias.reference);
    setFormAlias(alias.alias);
    setFormError('');
  };
  const cancelForm = () => {
    setFormMode('idle');
    setFormError('');
  };

  const handleSaveAlias = async () => {
    if (!formReference.trim() || !formAlias.trim()) {
      setFormError(t('referenceAliases.errors.emptyFields'));
      return;
    }
    if (!effectiveCompany) {
      setFormError(t('referenceAliases.errors.noCompany'));
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const result = await client.aliasWrite({
        companyName: effectiveCompany,
        reference: formReference.trim(),
        alias: formAlias.trim(),
      });
      if (!result.ok) {
        setFormError(
          result.code === 409
            ? t('referenceAliases.errors.duplicateAlias')
            : t('referenceAliases.errors.default')
        );
        return;
      }
      setFormMode('idle');
      aliasesCall.retry();
      onAliasChange();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (reference: string) => {
    setDeleteError('');
    try {
      const result = await client.aliasDelete(reference, effectiveCompany || undefined);
      if (!result.ok) {
        setDeleteError(t('referenceAliases.errors.default'));
        return;
      }
      aliasesCall.retry();
      onAliasChange();
    } catch {
      setDeleteError(t('referenceAliases.errors.default'));
    }
  };

  const handleCompanyChange = (company: string) => {
    setSelectedCompany(company);
    setFormMode('idle');
    setPendingMode(null);
  };

  return (
    <div className="space-y-6">
      {/* Super-admin: company picker */}
      {isSuperAdmin && (
        <Card padding="sm" className="space-y-2">
          <Label id="label-company" htmlFor="select-company" className="text-sm font-medium">
            {t('referenceAliases.companyPicker.label')}
          </Label>
          <Select value={selectedCompany} onValueChange={handleCompanyChange}>
            <SelectTrigger id="select-company" aria-labelledby="label-company" className="w-64">
              <SelectValue placeholder={t('referenceAliases.companyPicker.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {companiesCall.isLoading ? (
                <SelectItem value="__loading__" disabled>
                  {t('referenceAliases.table.loading')}
                </SelectItem>
              ) : (
                companies.map((c) => (
                  <SelectItem key={c.name} value={c.name}>
                    {c.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          {companiesCall.error && (
            <Callout tone="error" size="inline">
              {t('referenceAliases.errors.default')}
            </Callout>
          )}
        </Card>
      )}

      {effectiveCompany ? (
        <>
          {/* Display mode */}
          <Card padding="sm" className="space-y-3">
            <p className="text-sm font-medium text-foreground">
              {t('referenceAliases.displayMode.title')}
            </p>
            {settingsCall.error && (
              <Callout tone="error" size="inline">
                {t('referenceAliases.errors.default')}
              </Callout>
            )}
            <div className="flex items-center gap-6">
              {(['reference', 'alias'] as const).map((mode) => (
                <label key={mode} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="displayMode"
                    value={mode}
                    checked={currentMode === mode}
                    onChange={() => setPendingMode(mode)}
                    className="accent-primary"
                    disabled={settingsCall.isLoading}
                  />
                  <span className="text-sm">{t(`referenceAliases.displayMode.${mode}`)}</span>
                </label>
              ))}
              <Button
                size="sm"
                onClick={handleSaveMode}
                disabled={settingsCall.isLoading}
                loading={savingMode}
              >
                {t('referenceAliases.displayMode.save')}
              </Button>
            </div>
            {saveModeError && (
              <Callout tone="error" size="inline">
                {saveModeError}
              </Callout>
            )}
          </Card>

          {/* Alias table */}
          <Card padding="none" className="overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
              <span className="text-sm font-medium text-foreground">
                {t('referenceAliases.tabs.title')}
              </span>
              {formMode === 'idle' && (
                <Button size="sm" variant="outline" onClick={openAdd}>
                  <Plus aria-hidden />
                  {t('referenceAliases.form.add')}
                </Button>
              )}
            </div>
            {deleteError && (
              <Callout tone="error" size="inline" className="px-4 pt-2">
                {deleteError}
              </Callout>
            )}

            {aliasesCall.isLoading ? (
              <div className="p-4 space-y-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : aliasesCall.error ? (
              <Callout tone="error" size="inline" className="p-4">
                {t('referenceAliases.errors.default')}
              </Callout>
            ) : aliases.length === 0 ? (
              <EmptyState size="compact" className="m-4" title={t('referenceAliases.table.empty')} />
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 bg-muted/40">
                    <th className="text-left px-4 py-2 text-muted-foreground font-medium">
                      {t('referenceAliases.table.reference')}
                    </th>
                    <th className="text-left px-4 py-2 text-muted-foreground font-medium">
                      {t('referenceAliases.table.alias')}
                    </th>
                    <th className="text-left px-4 py-2 text-muted-foreground font-medium">
                      {t('referenceAliases.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {aliases.map((alias) => (
                    <tr
                      key={alias.reference}
                      className="border-b border-border/40 last:border-0 hover:bg-muted/20"
                    >
                      <td className="px-4 py-2 font-mono text-foreground">{alias.reference}</td>
                      <td className="px-4 py-2 text-foreground">{alias.alias}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <IconButton
                            icon={Pencil}
                            onClick={() => openEdit(alias)}
                            label={t('referenceAliases.form.edit')}
                          />
                          <IconButton
                            icon={Trash2}
                            variant="ghost-destructive"
                            onClick={() => setPendingDelete(alias.reference)}
                            label={t('referenceAliases.form.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {/* Add / Edit form */}
          {formMode !== 'idle' && (
            <Card padding="sm" className="space-y-4">
              <p className="text-sm font-semibold text-foreground">
                {formMode === 'add' ? t('referenceAliases.form.add') : t('referenceAliases.form.edit')}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label
                    id="label-reference"
                    htmlFor="select-reference"
                    className="text-xs"
                  >
                    {t('referenceAliases.table.reference')}
                  </Label>
                  {formMode === 'add' ? (
                    <Select value={formReference} onValueChange={setFormReference}>
                      <SelectTrigger id="select-reference" aria-labelledby="label-reference">
                        <SelectValue placeholder={t('referenceAliases.form.referencePlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {toolsCall.isLoading ? (
                          <SelectItem value="__loading__" disabled>
                            {t('referenceAliases.table.loading')}
                          </SelectItem>
                        ) : toolsCall.error ? (
                          <SelectItem value="__error__" disabled>
                            {t('referenceAliases.errors.default')}
                          </SelectItem>
                        ) : (
                          distinctReferences.map((ref) => (
                            <SelectItem key={ref} value={ref}>
                              {ref}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={formReference} readOnly className="bg-muted/40 cursor-not-allowed" />
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label id="label-alias" htmlFor="input-alias" className="text-xs">
                    {t('referenceAliases.table.alias')}
                  </Label>
                  <Input
                    id="input-alias"
                    value={formAlias}
                    onChange={(e) => setFormAlias(e.target.value)}
                    placeholder={t('referenceAliases.form.aliasPlaceholder')}
                  />
                </div>
              </div>

              {formError && (
                <Callout tone="error" size="inline">
                  {formError}
                </Callout>
              )}

              <div className="flex items-center gap-2">
                <Button size="sm" onClick={handleSaveAlias} loading={submitting}>
                  {t('referenceAliases.form.save')}
                </Button>
                <Button size="sm" variant="outline" onClick={cancelForm} disabled={submitting}>
                  {t('referenceAliases.form.cancel')}
                </Button>
              </div>
            </Card>
          )}

          {/* Delete confirmation dialog */}
          <ConfirmDialog
            open={pendingDelete !== null}
            title={t('referenceAliases.form.delete')}
            description={pendingDelete}
            confirmLabel={t('referenceAliases.form.delete')}
            cancelLabel={t('referenceAliases.form.cancel')}
            onConfirm={() => {
              if (pendingDelete) {
                void handleDelete(pendingDelete);
                setPendingDelete(null);
              }
            }}
            onCancel={() => setPendingDelete(null)}
          />
        </>
      ) : isSuperAdmin ? (
        <p className="text-sm text-muted-foreground">{t('referenceAliases.companyPicker.prompt')}</p>
      ) : null}
    </div>
  );
}
